import React, { useMemo } from 'react';
import { Loader2, Calendar } from 'lucide-react';
import { useFieldUpcoming, type FieldTask } from '../../hooks/useFieldToday';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstantDay, formatInstantTime } from '../../lib/calendar-date';

type Vertical = 'trucking' | 'surface_rentals';

const dot: Record<Vertical, string> = {
  trucking: 'bg-fieldiq-accent',
  surface_rentals: 'bg-blue-400',
};

// scheduledAt is an instant; the day it is grouped under and the time shown
// beside it are the tenant's (companies.time_zone), not the phone's.
function groupByDate(tasks: FieldTask[], timeZone: string | null): { date: string; tasks: FieldTask[] }[] {
  const map = new Map<string, FieldTask[]>();
  for (const task of tasks) {
    const key = task.scheduledAt
      ? formatInstantDay(task.scheduledAt, timeZone, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }, 'en-US') ?? '—'
      : 'Unscheduled';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(task);
  }
  return Array.from(map.entries()).map(([date, tasks]) => ({ date, tasks }));
}

function formatTime(iso: string | undefined, timeZone: string | null) {
  return formatInstantTime(iso, timeZone, { hour: 'numeric', minute: '2-digit' }) ?? '—';
}

export default function SchedulePage() {
  const { data: tasks = [], isLoading } = useFieldUpcoming(7);
  const timeZone = useTenantTimeZone();
  const grouped = useMemo(() => groupByDate(tasks, timeZone), [tasks, timeZone]);

  return (
    <div className="px-4 pt-4 pb-4">
      <div>
        {/* The page title is the shell's, beside its back arrow (fix/field-home-trucking-rentals). */}
        <p className="text-xs text-fieldiq-text">Forward view of your assigned work.</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-fieldiq-text" />
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-12">
          <Calendar size={40} className="mx-auto text-fieldiq-text/40 mb-3" />
          <p className="text-sm text-fieldiq-text">No upcoming work in the next 7 days</p>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          {grouped.map((day) => (
            <section key={day.date}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
                {day.date}
              </p>
              <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
                {day.tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 p-3.5">
                    <span className={`h-2 w-2 rounded-full ${dot[t.vertical as Vertical] ?? 'bg-fieldiq-accent'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{t.title}</p>
                      <p className="text-[11px] text-fieldiq-text truncate">{t.subtitle}</p>
                    </div>
                    <span className="text-xs font-semibold text-fieldiq-text tabular-nums shrink-0">
                      {formatTime(t.scheduledAt, timeZone)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
